import {graphql} from 'react-relay';
import type {Environment, MutationParameters, PayloadError} from 'relay-runtime';
import Util, {getValidationErrorMessageId} from '../../../util/util';
import {trackAndCommitMutation} from '../../../../../tracking/amplitude/TrackMutation';

export type SendPhaseSignoffRequestInput = {
	/** Relay global id of the phase (decoded server-side to the raw milestone id). */
	psaMilestoneId: string;
	/** Relay global id of the parent project. Required — anchors the request to a client org. */
	psaProjectId: string;
	/** portal_users UUID for the approver. Not a Forecast person id, not a Relay id. */
	approverPortalUserId: string;
	/** Calendar date (YYYY-MM-DD) or undefined for no deadline. */
	deadline?: string;
};

// The response has to name every field the write moves, not just the state, or Relay
// merges the new state onto the phase record and leaves the deadline, live-request
// approver, and previous changes-requested note as the CHANGES_REQUESTED row set
// them — so a resend flips the cell to Awaiting client and still renders the old
// approver and the old (probably past) deadline until an unrelated refetch. The
// resolver's `phase` re-reads getPhaseConfig, which already surfaces all four.
const mutation = graphql`
	mutation sendPhaseSignoffRequestMutation($input: SendPhaseSignoffRequestInput!) {
		sendPhaseSignoffRequest(input: $input) {
			phase {
				id
				portalConfig {
					signoffRequestState
					signoffRequestDeadline
					signoffRequestApproverPortalUserId
					signoffChangesRequestedNote
				}
			}
		}
	}
`;

const SendPhaseSignoffRequestMutation = {
	commit(
		environment: Environment,
		input: SendPhaseSignoffRequestInput,
		onSuccess: (response: unknown, errors: readonly PayloadError[] | null | undefined) => void,
		onError: (error: Error) => void
	) {
		return trackAndCommitMutation<MutationParameters>(environment, {
			mutation,
			variables: {
				input: {
					csrfToken: Util.getCsrfValue(),
					socketClientId: Util.getClientId(),
					...input,
				},
			},
			onCompleted: onSuccess,
			onError,
		} as never);
	},
};

/**
 * Open a client sign-off request on a phase. Always via Util.CommitMutation — never
 * react-relay's commitMutation — so the correlation-id error modal and the logged-out
 * handling still apply.
 *
 * A recognised refusal (APPROVER_NOT_ELIGIBLE, SIGNOFF_REQUEST_ALREADY_OPEN, etc.) is
 * surfaced inline via the `onError` callback with a translation-id string; anything
 * else falls through to the framework's shared error modal. The refusal path stays
 * open so the composed deadline and approver survive — routing to the global modal
 * would either replace this one or render behind it, destroying or hiding the state.
 *
 * `retry=false` is deliberate: this write emails the client and records an audit row.
 * A transient network failure that lost the response would, on retry, arrive as a
 * second SIGNOFF_REQUEST_ALREADY_OPEN — which is precisely the surface this refusal
 * mapping exists to explain — but the modal's `submitting` guard cannot prevent the
 * framework's own 500ms retry, so the safe direction is to fail loudly and let the
 * user re-press Send.
 */
export function sendPhaseSignoffRequest(
	input: SendPhaseSignoffRequestInput,
	onSuccess: () => void,
	onError?: (validationMessageId: string | null) => void
): void {
	Util.CommitMutation(
		SendPhaseSignoffRequestMutation,
		input,
		onSuccess,
		false,
		null,
		(transaction: unknown, onFailure: (t: unknown) => unknown) => {
			const validationMessageId = getValidationErrorMessageId(String(transaction));
			if (validationMessageId) {
				if (onError) onError(validationMessageId);
				return;
			}
			const retryScheduled = onFailure(transaction);
			if (retryScheduled !== true && onError) onError(null);
		}
	);
}
